import React, { useState } from 'react';

const sermonPrepTools = [
  { icon: '📖', title: 'Sermon Prep',        desc: 'Generate full sermon outlines anchored to any Scripture passage in seconds.' },
  { icon: '📚', title: 'Bible Study',         desc: 'Create complete small group or Sunday school lesson guides with discussion questions.' },
  { icon: '🗓️', title: 'Series Planner',      desc: 'Plan a cohesive multi-week sermon series with a unified arc and weekly breakdowns.' },
  { icon: '✍️', title: 'Devotional Writer',   desc: 'Create daily devotionals with Scripture, reflection prompts, and closing prayer.' },
];

const commCareTools = [
  { icon: '📢', title: 'Announcement Writer', desc: 'Create social media, bulletin, and email announcements for any church event.' },
  { icon: '👥', title: 'Discussion Builder',  desc: 'Build complete small group discussion guides tied directly to your Sunday sermon.' },
  { icon: '🤝', title: 'Pastoral Care',       desc: 'Compose compassionate, Scripture-anchored messages for sensitive pastoral situations.' },
  { icon: '🙏', title: 'Prayer Response',     desc: 'Write warm, personal responses to written prayer requests from your congregation.' },
  { icon: '💬', title: 'Evangelism Guide',    desc: 'Create natural conversation guides for sharing the gospel in everyday situations.' },
  { icon: '🎉', title: 'Event Promo',         desc: 'Generate a full multi-channel promotion package for any church event or ministry.' },
];

const faqs = [
  {
    q: 'Is this theologically safe to use?',
    a: 'Every tool requires you to provide the Scripture passage or topic. The AI builds around what you give it — it never generates theology on its own. You review everything before it reaches your congregation.',
  },
  {
    q: 'Will this replace my sermon preparation?',
    a: 'No. It replaces the blank page. You still study, pray, and preach. The toolkit handles outlines, announcements, and discussion guides so you spend less time formatting and more time with God and people.',
  },
  {
    q: 'Is it really free?',
    a: 'Yes. 5 generations per month, all 10 tools, no credit card required. Pastor Pro ($15/month) removes the limit entirely.',
  },
  {
    q: 'What denominations is this built for?',
    a: 'All of them. The tools are theologically neutral on secondary issues. You bring your tradition — the toolkit serves it.',
  },
  {
    q: 'Can I use this on my phone?',
    a: 'Yes. Ministry AI Toolkit works great on mobile. On iPhone, open the site in Safari, tap the Share button, and select "Add to Home Screen" — it installs like a real app with its own icon. On Android, tap the menu in Chrome and select "Add to Home Screen". No App Store required.',
  },
];

const iphoneSteps = [
  { icon: '1', text: 'Open this page in Safari' },
  { icon: '2', text: 'Tap the Share button (□↑) at the bottom of the screen' },
  { icon: '3', text: 'Scroll down and tap "Add to Home Screen"' },
  { icon: '4', text: 'Tap "Add" to confirm' },
];

const androidSteps = [
  { icon: '1', text: 'Open this page in Chrome' },
  { icon: '2', text: 'Tap the menu (⋮) in the top right' },
  { icon: '3', text: 'Tap "Add to Home Screen"' },
  { icon: '4', text: 'Tap "Add" to confirm' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 py-5">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-left gap-4"
      >
        <span className="font-semibold text-base" style={{ color: '#1B3A5C' }}>{q}</span>
        <span className="text-gray-400 text-lg flex-shrink-0">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <p className="mt-3 text-gray-500 text-sm leading-relaxed">{a}</p>
      )}
    </div>
  );
}

function InstallModal({ platform, onClose }) {
  const steps = platform === 'iphone' ? iphoneSteps : androidSteps;
  const title = platform === 'iphone' ? 'Add to iPhone Home Screen' : 'Add to Android Home Screen';
  const icon = platform === 'iphone' ? '🍎' : '🤖';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-lg" style={{ color: '#1B3A5C' }}>
            {icon} {title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <ol className="space-y-3">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white"
                style={{ backgroundColor: '#1B3A5C' }}
              >
                {i + 1}
              </span>
              <span className="text-gray-600 text-sm pt-1">{step.text}</span>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-xs text-gray-400 text-center">
          The app will appear on your home screen with the Ministry AI icon.
        </p>
        <button
          onClick={onClose}
          className="mt-4 w-full py-2.5 rounded-xl text-white font-semibold text-sm hover:opacity-90 transition-opacity"
          style={{ backgroundColor: '#C8921A' }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

export default function Landing({ onGetStarted, onLogin }) {
  const [installModal, setInstallModal] = useState(null); // 'iphone' | 'android' | null

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {installModal && (
        <InstallModal platform={installModal} onClose={() => setInstallModal(null)} />
      )}

      {/* Header */}
      <header className="py-5 px-8 border-b border-gray-100 flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: '#1B3A5C' }}>Ministry AI Toolkit</h1>
        <button
          onClick={onLogin}
          className="text-sm font-medium px-4 py-2 rounded-lg border transition-colors hover:bg-gray-50"
          style={{ color: '#1B3A5C', borderColor: '#1B3A5C' }}
        >
          Sign In
        </button>
      </header>

      <main className="flex-1 flex flex-col">

        {/* Hero */}
        <section className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <div className="inline-block mb-4 px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: '#FDF3DC', color: '#C8921A' }}>
            AI Tools for Pastors
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" style={{ color: '#1B3A5C' }}>
            Spend Less Time Preparing.<br />More Time Serving.
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-xl mb-10">
            10 AI-powered tools to help pastors with sermons, care, communication, and more — all anchored in Scripture.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={onGetStarted}
              className="px-10 py-4 rounded-lg text-white font-semibold text-lg transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#C8921A' }}
            >
              Get Started Free
            </button>
            <button
              onClick={onLogin}
              className="px-10 py-4 rounded-lg font-semibold text-lg transition-colors border hover:bg-gray-50"
              style={{ color: '#1B3A5C', borderColor: '#1B3A5C' }}
            >
              Sign In
            </button>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-5 border-y border-gray-100" style={{ backgroundColor: '#F9FAFB' }}>
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-gray-500 px-6">
            <span className="font-semibold" style={{ color: '#1B3A5C' }}>10 Ministry Tools</span>
            <span style={{ color: '#C8921A' }}>·</span>
            <span>Free to Start</span>
            <span style={{ color: '#C8921A' }}>·</span>
            <span>No Credit Card Required</span>
            <span style={{ color: '#C8921A' }}>·</span>
            <span>Trusted by Pastors</span>
            <span style={{ color: '#C8921A' }}>·</span>
            <span>📱 Works on Mobile</span>
          </div>
        </section>

        {/* All 10 Tools */}
        <section className="px-6 py-16 max-w-5xl mx-auto w-full">
          <h3 className="text-2xl font-bold text-center mb-2" style={{ color: '#1B3A5C' }}>Everything You Need in One Place</h3>
          <p className="text-center text-gray-500 mb-12">From Sunday prep to pastoral care — built for real ministry work.</p>

          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-5 text-center">Sermon Preparation</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {sermonPrepTools.map((tool) => (
                <div key={tool.title} className="bg-gray-50 rounded-xl p-5 border border-gray-200 text-left hover:shadow-sm transition-shadow">
                  <div className="text-3xl mb-3">{tool.icon}</div>
                  <h4 className="font-semibold text-base mb-1" style={{ color: '#1B3A5C' }}>{tool.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{tool.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-5 text-center">Communication & Care</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {commCareTools.map((tool) => (
                <div key={tool.title} className="bg-gray-50 rounded-xl p-5 border border-gray-200 text-left hover:shadow-sm transition-shadow">
                  <div className="text-3xl mb-3">{tool.icon}</div>
                  <h4 className="font-semibold text-base mb-1" style={{ color: '#1B3A5C' }}>{tool.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{tool.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust / Disclaimer Section */}
        <section className="py-16 px-6 text-center" style={{ backgroundColor: '#F3F6FA' }}>
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-5" style={{ color: '#1B3A5C' }}>
              Built by a Pastor. Grounded in Scripture.
            </h3>
            <p className="text-gray-600 leading-relaxed text-base">
              Every tool in the Ministry AI Toolkit is anchored to Scripture. AI assists your preparation — your voice, your discernment, and your relationship with God are irreplaceable. This toolkit helps with the desk work. The rest is between you and God.
            </p>
          </div>
        </section>

        {/* Mobile Install Section */}
        <section className="py-12 px-6 max-w-2xl mx-auto w-full">
          <div className="rounded-2xl border border-gray-200 p-7 text-center" style={{ backgroundColor: '#F3F6FA' }}>
            <div className="text-4xl mb-3">📱</div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1B3A5C' }}>Works on Your Phone</h3>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              Install Ministry AI Toolkit on your iPhone or Android home screen for quick access between services.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setInstallModal('iphone')}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#1B3A5C' }}
              >
                <span>🍎</span>
                <span>iPhone — Add to Home Screen</span>
              </button>
              <button
                onClick={() => setInstallModal('android')}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#1B3A5C' }}
              >
                <span>🤖</span>
                <span>Android — Add to Home Screen</span>
              </button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 max-w-2xl mx-auto w-full">
          <h3 className="text-2xl font-bold text-center mb-10" style={{ color: '#1B3A5C' }}>Common Questions</h3>
          <div>
            {faqs.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-6 text-center" style={{ backgroundColor: '#1B3A5C' }}>
          <h3 className="text-2xl font-bold text-white mb-3">Ready to Spend More Time with God and People?</h3>
          <p className="text-blue-200 mb-8 text-sm">5 free generations per month. No credit card. Cancel anytime.</p>
          <button
            onClick={onGetStarted}
            className="px-10 py-4 rounded-lg font-semibold text-base hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#C8921A', color: 'white' }}
          >
            Get Started Free →
          </button>
        </section>

      </main>

      <footer className="py-5 text-center text-sm text-gray-400 border-t border-gray-100">
        A Matthew2819 Ministry Tool · <span style={{ color: '#C8921A' }}>Pastor Pro</span> — $15/month for unlimited access
      </footer>
    </div>
  );
}
